import type { LevelDefinition } from './Level.ts';

/**
 * Level 5 — the roofed goal.
 *
 * The goal sits directly below the reservoir, and the obvious move drops the whole blob
 * onto its roof: the chamber is sealed on top, so a positive magnet on the goal pulls the
 * fluid straight down onto a lid it cannot pass and parks it there, sixty world units
 * short, for the rest of the run. Nothing about the field is wrong. The route is.
 *
 * The way in is the hatch at the far right of the chamber floor, which means going around
 * the outside of the box — and the box has two outsides. The left one is a dead-end crack
 * that swallows a stream and holds it; the right one runs down to the hatch. So the
 * negative magnet is not aiming at the goal at all, it is choosing which side of an
 * obstacle the stream passes.
 *
 * Proven headlessly before it shipped: the intended pair solves in 6.6s, the obvious
 * single attractor settles at 0% having reached (355, 949), and a sweep of every
 * single-magnet placement on a 100wu grid — 270 of them — solves it zero times.
 */
export const level05: LevelDefinition = {
  id: 'level05',
  name: 'The lid',
  hint: 'It will sit on the roof of the goal all day. The way in is underneath.',
  magnetsAvailable: 2,
  fluid: {
    amount: 380,
    source: { x: 450, y: 400 },
    radius: 44,
  },
  goal: { x: 380, y: 1120, radius: 115, type: 'fluid', fraction: 0.45 },
  walls: [
    // Arena.
    { type: 'segment', x1: 120, y1: 240, x2: 880, y2: 240, thickness: 26 },
    { type: 'segment', x1: 880, y1: 240, x2: 880, y2: 1400, thickness: 26 },
    { type: 'segment', x1: 880, y1: 1400, x2: 120, y2: 1400, thickness: 26 },
    { type: 'segment', x1: 120, y1: 1400, x2: 120, y2: 240, thickness: 26 },

    // The box around the goal: solid lid, left wall down to the floor, right wall stopping
    // short of it, and a floor with the hatch at its right-hand end.
    { type: 'segment', x1: 200, y1: 1000, x2: 700, y2: 1000, thickness: 26 },
    { type: 'segment', x1: 200, y1: 1000, x2: 200, y2: 1400, thickness: 26 },
    { type: 'segment', x1: 700, y1: 1000, x2: 700, y2: 1250, thickness: 26 },
    { type: 'segment', x1: 200, y1: 1250, x2: 560, y2: 1250, thickness: 26 },
    { type: 'segment', x1: 560, y1: 1250, x2: 560, y2: 1400, thickness: 26 },
  ],
};
